import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as MenuPrimitive from '@radix-ui/react-dropdown-menu';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Check, X } from 'lucide-react';
import { forwardRef, useState, type ComponentPropsWithoutRef, type ElementRef, type FormEvent, type ReactNode } from 'react';
import { LayerContext, useLayerContainer } from './layer';
import { Button, IconButton } from './button';
import { cn } from './cn';
import { RoadSign } from './signs';

/** Radix links a Description itself; without one it wants aria-describedby explicitly undefined. */
const noDescription = (description: ReactNode) => (description ? {} : { 'aria-describedby': undefined });

const DIALOG_WIDTH = {
  sm: 'w-[min(420px,calc(100vw-32px))]',
  md: 'w-[min(560px,calc(100vw-32px))]',
  lg: 'w-[min(760px,calc(100vw-32px))]',
  xl: 'w-[min(1040px,calc(100vw-32px))]',
};

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  /** The close button's accessible name. */
  closeLabel: string;
  size?: keyof typeof DIALOG_WIDTH;
  children?: ReactNode;
  /** Actions, end-aligned: the primary one last. */
  footer?: ReactNode;
  /** Wrap the body and footer in a form (Enter submits). */
  onSubmit?: (event: FormEvent<HTMLFormElement>) => void;
  /** Padding around the body; off for edge-to-edge content (a map). */
  padded?: boolean;
}

/** Modal: radius 16, scrim behind, shadow-4. Centred without transforms, so it is the same in both directions. */
export function Dialog({ open, onOpenChange, title, description, closeLabel, size = 'md', children, footer, onSubmit, padded = true }: DialogProps) {
  const [layer, setLayer] = useState<HTMLDivElement | null>(null);
  const body = (
    <>
      <div className={cn('min-h-0 overflow-y-auto', padded && 'px-5 pb-5 pt-1')}>{children}</div>
      {footer && <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line bg-surface px-5 py-3">{footer}</div>}
    </>
  );
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="vlm-fade fixed inset-0 z-modal bg-scrim" />
        <DialogPrimitive.Content
          ref={setLayer}
          {...noDescription(description)}
          className={cn(
            'fixed inset-0 z-modal m-auto grid h-fit max-h-[calc(100dvh-32px)] grid-rows-[auto_minmax(0,1fr)] overflow-hidden rounded-xl border border-line bg-surface-raised shadow-4 animate-arrive',
            DIALOG_WIDTH[size],
          )}
        >
          <LayerContext.Provider value={layer}>
            <div className="flex items-start justify-between gap-4 px-5 pb-3 pt-4">
              <div className="grid min-w-0 gap-1">
                <DialogPrimitive.Title className="text-h3 text-ink">{title}</DialogPrimitive.Title>
                {description && <DialogPrimitive.Description className="text-body-sm text-ink-muted">{description}</DialogPrimitive.Description>}
              </div>
              <DialogPrimitive.Close asChild>
                <IconButton label={closeLabel} tooltip={false} size="sm" icon={<X size={18} aria-hidden="true" />} className="-me-1.5 -mt-0.5" />
              </DialogPrimitive.Close>
            </div>
            {onSubmit ? (
              <form noValidate onSubmit={onSubmit} className="grid min-h-0 grid-rows-[minmax(0,1fr)_auto]">
                {body}
              </form>
            ) : (
              <div className="grid min-h-0 grid-rows-[minmax(0,1fr)_auto]">{body}</div>
            )}
          </LayerContext.Provider>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  body?: ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  closeLabel: string;
  /** danger: a destructive step — hazard sign and a hazard button. */
  tone?: 'action' | 'danger';
  /** May return a promise; the dialog closes when it resolves and stays open (for the caller's toast) when it rejects. */
  onConfirm: () => unknown;
}

export function ConfirmDialog({ open, onOpenChange, title, body, confirmLabel, cancelLabel, closeLabel, tone = 'action', onConfirm }: ConfirmDialogProps) {
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    setBusy(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch {
      // the caller reports the failure
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !busy && onOpenChange(next)}
      size="sm"
      closeLabel={closeLabel}
      title={
        <span className="flex items-center gap-2.5">
          {tone === 'danger' && <RoadSign sign="hazard" size={22} />}
          {title}
        </span>
      }
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button variant={tone === 'danger' ? 'danger' : 'action'} loading={busy} onClick={confirm}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {body && <div className="text-body text-ink-muted">{body}</div>}
    </Dialog>
  );
}

export interface SheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  closeLabel: string;
  children?: ReactNode;
  footer?: ReactNode;
  width?: number;
}

/** A panel from the end edge (it comes from the left in Arabic), for a record's details. */
export function Sheet({ open, onOpenChange, title, description, closeLabel, children, footer, width = 480 }: SheetProps) {
  const [layer, setLayer] = useState<HTMLDivElement | null>(null);
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="vlm-fade fixed inset-0 z-sheet bg-scrim" />
        <DialogPrimitive.Content
          ref={setLayer}
          {...noDescription(description)}
          className="vlm-sheet fixed inset-y-0 end-0 z-sheet grid max-w-full grid-rows-[auto_minmax(0,1fr)_auto] border-s border-line bg-surface-raised shadow-4"
          style={{ width }}
        >
          <LayerContext.Provider value={layer}>
            <div className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
              <div className="grid min-w-0 gap-1">
                <DialogPrimitive.Title className="text-h3 text-ink">{title}</DialogPrimitive.Title>
                {description && <DialogPrimitive.Description className="text-body-sm text-ink-muted">{description}</DialogPrimitive.Description>}
              </div>
              <DialogPrimitive.Close asChild>
                <IconButton label={closeLabel} tooltip={false} size="sm" icon={<X size={18} aria-hidden="true" />} className="-me-1.5" />
              </DialogPrimitive.Close>
            </div>
            <div className="min-h-0 overflow-y-auto">{children}</div>
            {footer ? <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line px-5 py-3">{footer}</div> : <span />}
          </LayerContext.Provider>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

// ── menus ────────────────────────────────────────────────────────────────────

export const DropdownMenu = MenuPrimitive.Root;
export const DropdownMenuTrigger = MenuPrimitive.Trigger;
export const DropdownMenuGroup = MenuPrimitive.Group;
export const DropdownMenuRadioGroup = MenuPrimitive.RadioGroup;

const MENU_SURFACE = 'vlm-pop z-dropdown min-w-[220px] overflow-hidden rounded-md border border-line bg-surface-raised p-1 shadow-3';
const MENU_ITEM =
  'relative flex min-h-9 cursor-pointer select-none items-center gap-2.5 rounded-sm px-2.5 py-1.5 text-body-sm font-semibold text-ink outline-none data-[disabled]:pointer-events-none data-[highlighted]:bg-hover data-[disabled]:text-ink-disabled';

export const DropdownMenuContent = forwardRef<ElementRef<typeof MenuPrimitive.Content>, ComponentPropsWithoutRef<typeof MenuPrimitive.Content>>(
  function DropdownMenuContent({ className, align = 'end', sideOffset = 6, ...rest }, ref) {
    const container = useLayerContainer();
    return (
      <MenuPrimitive.Portal container={container}>
        <MenuPrimitive.Content ref={ref} align={align} sideOffset={sideOffset} collisionPadding={8} className={cn(MENU_SURFACE, className)} {...rest} />
      </MenuPrimitive.Portal>
    );
  },
);

export const DropdownMenuItem = forwardRef<
  ElementRef<typeof MenuPrimitive.Item>,
  ComponentPropsWithoutRef<typeof MenuPrimitive.Item> & { icon?: ReactNode; tone?: 'default' | 'danger' }
>(function DropdownMenuItem({ className, icon, tone = 'default', children, ...rest }, ref) {
  return (
    <MenuPrimitive.Item ref={ref} className={cn(MENU_ITEM, tone === 'danger' && 'text-hazard-ink', className)} {...rest}>
      {icon && <span className="flex shrink-0 text-ink-subtle">{icon}</span>}
      {children}
    </MenuPrimitive.Item>
  );
});

export const DropdownMenuRadioItem = forwardRef<ElementRef<typeof MenuPrimitive.RadioItem>, ComponentPropsWithoutRef<typeof MenuPrimitive.RadioItem> & { icon?: ReactNode }>(
  function DropdownMenuRadioItem({ className, icon, children, ...rest }, ref) {
    return (
      <MenuPrimitive.RadioItem ref={ref} className={cn(MENU_ITEM, 'pe-8', className)} {...rest}>
        {icon && <span className="flex shrink-0 text-ink-subtle">{icon}</span>}
        {children}
        <MenuPrimitive.ItemIndicator className="absolute end-2.5 flex text-ink">
          <Check size={16} aria-hidden="true" />
        </MenuPrimitive.ItemIndicator>
      </MenuPrimitive.RadioItem>
    );
  },
);

export function DropdownMenuLabel({ children, className }: { children: ReactNode; className?: string }) {
  return <MenuPrimitive.Label className={cn('px-2.5 pb-1 pt-2 text-sign uppercase text-ink-subtle', className)}>{children}</MenuPrimitive.Label>;
}

export function DropdownMenuSeparator() {
  return <MenuPrimitive.Separator className="-mx-1 my-1 h-px bg-line" />;
}

// ── popover ──────────────────────────────────────────────────────────────────

export const Popover = PopoverPrimitive.Root;
export const PopoverTrigger = PopoverPrimitive.Trigger;
export const PopoverClose = PopoverPrimitive.Close;

export const PopoverContent = forwardRef<ElementRef<typeof PopoverPrimitive.Content>, ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>>(
  function PopoverContent({ className, align = 'end', sideOffset = 6, ...rest }, ref) {
    const container = useLayerContainer();
    return (
      <PopoverPrimitive.Portal container={container}>
        <PopoverPrimitive.Content
          ref={ref}
          align={align}
          sideOffset={sideOffset}
          collisionPadding={8}
          className={cn('vlm-pop z-dropdown overflow-hidden rounded-lg border border-line bg-surface-raised shadow-3 outline-none', className)}
          {...rest}
        />
      </PopoverPrimitive.Portal>
    );
  },
);
